import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { QrCode, Printer, FileDown, Search, Save, Info, Settings2 } from "lucide-react";
import { API, isAdmin, useAuth } from "@/context/AuthContext";
import { GateInPanel } from "@/components/GateInPanel";
import { GateInRegister } from "@/components/GateInRegister";
import { ScanLookup } from "@/components/ScanLookup";
import { WeightSplitPanel } from "@/components/WeightSplitPanel";

const DEFAULT_CFG = { label_width_mm: 100, label_height_mm: 50, dpi: 203, darkness: 10, printer_name: "", printer_ip: "", copies: 1 };

function LabelCard({ it, doc, year }) {
  const isGate = it.inward_type === "GATE_IN";
  const isPO = it.inward_type === "PO" || isGate;
  const isProd = it.inward_type === "PRODUCTION";
  const key = `${it.item_no}${it.piece ? `-${it.piece}` : ""}`;
  return (
    <div className="barcode-label rounded-lg border border-slate-300 bg-white p-4 flex gap-4 items-center shadow-sm break-inside-avoid"
      data-testid={`barcode-label-${key}`}>
      <img src={it.qr_png} alt="QR" className="h-28 w-28 shrink-0" data-testid={`barcode-qr-${key}`} />
      <div className="min-w-0 flex-1 space-y-0.5">
        <div className="flex items-start justify-between gap-2">
          <p className="text-base font-extrabold text-slate-900 font-mono truncate" title={it.material}>{it.material}</p>
          {it.piece && (
            <span className="shrink-0 rounded-full bg-slate-900 text-white text-[11px] font-bold px-2 py-0.5 tabular-nums" data-testid={`barcode-piece-${key}`}>{it.piece} of {it.pieces}</span>
          )}
        </div>
        <p className="text-sm text-slate-600 leading-tight line-clamp-2" title={it.description}>{it.description || "—"}</p>
        <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 pt-1 text-xs text-slate-700">
          {isPO && (<>
            <span><span className="text-slate-400">PO No:</span> <b className="font-mono">{it.purchase_order}</b></span>
            <span><span className="text-slate-400">PO Line:</span> <b className="font-mono">{it.po_item || "—"}</b></span>
          </>)}
          {isProd && (<>
            <span><span className="text-slate-400">Prod Order:</span> <b className="font-mono">{it.mfg_order}</b></span>
            <span><span className="text-slate-400">Order Line:</span> <b className="font-mono">{it.mfg_order_item || "—"}</b></span>
          </>)}
          <span><span className="text-slate-400">Qty:</span> <b className="tabular-nums">{it.label_qty} {it.unit}</b>{it.piece && <span className="text-slate-400"> (line {it.qty})</span>}</span>
          <span><span className="text-slate-400">Plant:</span> <b>{it.plant || "—"}</b></span>
          <span className="col-span-2 truncate" title={it.wbs_description}>
            <span className="text-slate-400">WBS:</span> <b className="font-mono">{it.wbs_element || "—"}</b>
            {it.wbs_description && <span className="text-slate-600"> — {it.wbs_description}</span>}
            {it.wbs_source && <span className="no-print text-[9px] text-slate-400 ml-1" title={it.wbs_source === "production_order" ? "WBS taken from the production order header" : "WBS taken from earlier MB51 movements of this order"}>({it.wbs_source === "production_order" ? "from order" : "from MB51 history"})</span>}
          </span>
          {isGate
            ? <span className="col-span-2 text-[10px] font-semibold text-emerald-700">GATE IN · {it.gate_in_date}{it.supplier_name ? ` · ${it.supplier_name}` : it.supplier ? ` · Supplier ${it.supplier}` : ""} · GRN pending</span>
            : <span className="col-span-2 text-[10px] text-slate-400">MatDoc {doc}/{year} · Item {it.item_no} · Mvt {it.movement_type}</span>}
        </div>
      </div>
    </div>
  );
}

export default function BarcodePrinting() {
  const { user } = useAuth();
  const admin = isAdmin(user);
  const [tab, setTab] = useState("print");
  const [docNo, setDocNo] = useState("");
  const [year, setYear] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [cfg, setCfg] = useState(DEFAULT_CFG);
  const [savingCfg, setSavingCfg] = useState(false);
  const [zplBusy, setZplBusy] = useState(false);
  const [perUnit, setPerUnit] = useState(true);
  const [inwardOnly, setInwardOnly] = useState(true);
  const [mode, setMode] = useState("matdoc"); // "matdoc" | "po"
  const [pieceFrom, setPieceFrom] = useState("");
  const [pieceTo, setPieceTo] = useState("");
  const pieceRange = { ...(pieceFrom ? { piece_from: Number(pieceFrom) } : {}), ...(pieceTo ? { piece_to: Number(pieceTo) } : {}) };
  const [gate, setGate] = useState(null);
  const [splits, setSplits] = useState({});
  const splitsParam = (s = splits) => (Object.keys(s).length ? JSON.stringify(s) : undefined);
  const [overdueGateIns, setOverdueGateIns] = useState(0);
  useEffect(() => {
    axios.get(`${API}/inventory/gate-in-register`, { params: { limit: 500 } }).then((r) => setOverdueGateIns(r.data.overdue_count || 0)).catch(() => {});
  }, []);

  const loadCfg = useCallback(() => {
    axios.get(`${API}/inventory/barcode-config`).then((r) => setCfg({ ...DEFAULT_CFG, ...r.data })).catch(() => {});
  }, []);
  useEffect(() => { loadCfg(); }, [loadCfg]);

  const docParams = (extra = {}) => ({ doc: docNo.trim(), ...(year.trim() ? { year: year.trim() } : {}), per_unit: perUnit, inward_only: inwardOnly, splits: splitsParam(), ...pieceRange, ...extra });

  const fetchDoc = async (opts = {}, docOverride = null, yearOverride = null) => {
    const d = (docOverride ?? docNo).trim();
    if (!d) { toast.error("Enter a Material Document Number"); return; }
    setLoading(true); setSplits({});
    try {
      const y = (yearOverride ?? year).trim();
      const r = await axios.get(`${API}/inventory/material-document`, { params: { ...docParams(opts), splits: undefined, doc: d, ...(y ? { year: y } : {}) } });
      setResult(r.data);
      if (!r.data.found || !(r.data.items || []).length) {
        toast.warning((r.data.errors && r.data.errors[0]) || "No items found for this Material Document");
      } else if (!opts.per_unit_toggle) {
        toast.success(`${r.data.items.length} item(s) → ${r.data.label_count} label(s) loaded${r.data.source === "demo" ? " (demo data)" : ""}`);
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not fetch the Material Document"); }
    finally { setLoading(false); }
  };

  const rebuild = async (extra) => {
    if (!result?.items?.length) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/inventory/material-document`, { params: docParams(extra) });
      setResult(r.data);
    } catch { toast.error("Could not rebuild labels"); }
    finally { setLoading(false); }
  };
  const togglePerUnit = async () => {
    const next = !perUnit; setPerUnit(next);
    if (mode === "po") {
      if (!gate?.lines?.length) return;
      setLoading(true);
      try {
        const r = await axios.post(`${API}/inventory/gate-in-labels`, { po: gate.po, lines: gate.lines, gate_in_date: gate.gate_in_date, per_unit: next, ...pieceRange });
        setGate({ ...r.data, po: gate.po, lines: gate.lines });
      } catch { toast.error("Could not rebuild labels"); }
      finally { setLoading(false); }
      return;
    }
    rebuild({ per_unit: next });
  };
  const applyRange = async (from, to) => {
    const rng = { ...(from ? { piece_from: Number(from) } : {}), ...(to ? { piece_to: Number(to) } : {}) };
    if (mode === "po") {
      if (!gate?.lines?.length) return;
      setLoading(true);
      try {
        const r = await axios.post(`${API}/inventory/gate-in-labels`, { po: gate.po, lines: gate.lines, gate_in_date: gate.gate_in_date, per_unit: perUnit, piece_from: null, piece_to: null, ...rng });
        setGate({ ...r.data, po: gate.po, lines: gate.lines });
      } catch { toast.error("Could not apply piece range"); }
      finally { setLoading(false); }
      return;
    }
    if (!result?.items?.length) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/inventory/material-document`, { params: { doc: docNo.trim(), ...(year.trim() ? { year: year.trim() } : {}), per_unit: perUnit, inward_only: inwardOnly, splits: splitsParam(), ...rng } });
      setResult(r.data);
    } catch { toast.error("Could not apply piece range"); }
    finally { setLoading(false); }
  };
  const reprintGateIn = async (entry) => {
    setLoading(true);
    try {
      const r = await axios.post(`${API}/inventory/gate-in-register/${entry.id}/labels`, null, { params: { per_unit: perUnit, ...pieceRange } });
      setGate({ ...r.data, po: entry.po, lines: r.data.lines, gate_in_date: r.data.gate_in_date });
      setMode("po"); setTab("print");
      toast.success(`${r.data.label_count} label(s) ready to reprint (Gate-In ${r.data.gate_in_date})`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not rebuild the labels"); }
    finally { setLoading(false); }
  };
  const openDocFromRegister = (d) => {
    setDocNo(d.doc); setYear(d.year || ""); setMode("matdoc"); setTab("print"); setResult(null);
    fetchDoc({}, d.doc, d.year || "");
  };
  const toggleInwardOnly = () => { const next = !inwardOnly; setInwardOnly(next); rebuild({ inward_only: next }); };

  const applySplits = async (next) => {
    if (mode !== "po") { setSplits(next); rebuild({ splits: splitsParam(next) }); return; }
    if (!gate?.lines?.length) return;
    const lines = gate.lines.map((l) => ({ ...l, weights: next[String(l.item).replace(/^0+/, "")] || null }));
    setLoading(true);
    try {
      const regId = gate.register_id || gate.entry_id || null;
      const r = await axios.post(`${API}/inventory/gate-in-labels`, { po: gate.po, lines, gate_in_date: gate.gate_in_date, per_unit: perUnit, register_id: regId, ...pieceRange });
      setGate({ ...r.data, po: gate.po, lines, register_id: regId, reprint: gate.reprint });
      toast.success(`${r.data.label_count} label(s) ready`);
    } catch { toast.error("Could not apply the sticker split"); }
    finally { setLoading(false); }
  };

  const printLabels = () => {
    if (mode === "po" ? !gate?.labels?.length : !result?.items?.length) return;
    window.print();
  };

  const downloadZpl = async () => {
    if (mode === "po" ? !gate?.labels?.length : !result?.items?.length) return;
    setZplBusy(true);
    try {
      const res = mode === "po"
        ? await axios.post(`${API}/inventory/gate-in-labels/zpl`, { po: gate.po, lines: gate.lines, gate_in_date: gate.gate_in_date, per_unit: perUnit, ...pieceRange }, { responseType: "blob" })
        : await axios.get(`${API}/inventory/material-document/zpl`, { params: docParams(), responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = mode === "po" ? `gatein-${gate.po}.zpl` : `labels-${docNo.trim()}.zpl`; a.click();
      URL.revokeObjectURL(url);
      toast.success("ZPL file downloaded — send it to your Zebra label printer");
    } catch { toast.error("Could not generate ZPL"); }
    finally { setZplBusy(false); }
  };

  const saveCfg = async () => {
    setSavingCfg(true);
    try {
      await axios.post(`${API}/inventory/barcode-config`, {
        label_width_mm: Number(cfg.label_width_mm), label_height_mm: Number(cfg.label_height_mm),
        dpi: Number(cfg.dpi), darkness: Number(cfg.darkness), copies: Number(cfg.copies),
        printer_name: cfg.printer_name || "", printer_ip: cfg.printer_ip || "",
      });
      toast.success("Printer configuration saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save configuration"); }
    finally { setSavingCfg(false); }
  };

  const items = result?.items || [];
  const labels = useMemo(() => (mode === "po" ? (gate?.labels || []) : (result?.labels || [])), [mode, gate, result]);
  const copies = Math.max(1, Number(cfg.copies) || 1);
  // Repeat each label 'copies' times for the browser print preview so what you see == what prints.
  const printItems = labels.flatMap((it) => Array.from({ length: copies }, (_, k) => ({ ...it, _copy: k })));
  const weightLines = useMemo(() => {
    const seen = {};
    labels.forEach((l) => { if (l.is_weight && !seen[l.item_no]) seen[l.item_no] = { item_no: l.item_no, material: l.material, description: l.description, unit: l.unit, qty: l.qty }; });
    return Object.values(seen);
  }, [labels]);
  const activeSplits = useMemo(() => {
    if (mode !== "po") return splits;
    const s = {};
    (gate?.lines || []).forEach((l) => { if (l.weights?.length > 1) s[String(l.item).replace(/^0+/, "")] = l.weights; });
    return s;
  }, [mode, splits, gate]);
  const splitPanel = <WeightSplitPanel lines={weightLines} splits={activeSplits} onApply={applySplits} loading={loading} />;
  const wbsHint = (result?.errors || []).find((e) => e.startsWith("WBS descriptions unavailable"));
  const orderHint = (result?.errors || []).find((e) => e.startsWith("Production-order WBS unavailable"));

  return (
    <div className="space-y-6" data-testid="barcode-printing-page">
      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          #barcode-print-area, #barcode-print-area * { visibility: visible !important; }
          #barcode-print-area { position: absolute; left: 0; top: 0; width: 100%; padding: 0; }
          .barcode-label { break-inside: avoid; page-break-inside: avoid; border: 1px solid #cbd5e1 !important; margin-bottom: 8px; }
          .no-print { display: none !important; }
        }
      `}</style>

      <div className="no-print">
        <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 flex items-center gap-2">
          <QrCode className="h-7 w-7 text-sky-700" /> Barcode Printing
        </h1>
        <p className="text-sm text-slate-500 mt-1">Enter a GRN or Production <b>Material Document Number</b> to pull its items from SAP and generate QR-code labels — Item Code, Description, PO No &amp; Line (for PO inward) or Production Order &amp; Line (for production inward), Qty, WBS with description and Plant. One label per unit received (“3 of 10”), or one per line. Print via your browser or download ZPL for a Zebra label printer.</p>
      </div>

      <div className="no-print">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="print" data-testid="barcode-tab-print">Print Labels</TabsTrigger>
            <TabsTrigger value="register" data-testid="barcode-tab-register" className="gap-1.5">Gate-In Register{overdueGateIns > 0 && <span className="rounded-full bg-red-600 text-white text-[10px] font-bold px-1.5 py-px leading-4" data-testid="barcode-tab-register-badge">{overdueGateIns}</span>}</TabsTrigger>
            <TabsTrigger value="scan" data-testid="barcode-tab-scan">Scan Lookup</TabsTrigger>
            <TabsTrigger value="config" data-testid="barcode-tab-config">Printer Setup</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {tab === "print" ? (
        <>
          <div className="no-print flex flex-wrap items-center gap-3">
            <div className="inline-flex rounded-lg border border-slate-300 bg-slate-100 p-0.5 text-sm" role="tablist" data-testid="barcode-mode-toggle">
              <button type="button" onClick={() => setMode("matdoc")} data-testid="barcode-mode-matdoc"
                className={`px-3 py-1.5 rounded-md font-medium transition-colors ${mode === "matdoc" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-800"}`}>Material Document</button>
              <button type="button" onClick={() => setMode("po")} data-testid="barcode-mode-po"
                className={`px-3 py-1.5 rounded-md font-medium transition-colors ${mode === "po" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-800"}`}>PO No / Line — Gate In</button>
            </div>
            <label className="flex items-center gap-2 h-9 px-3 rounded-md border border-slate-300 bg-white text-sm text-slate-700 cursor-pointer select-none" title="Print one label per unit received, numbered 1 of N … N of N">
              <input type="checkbox" checked={perUnit} onChange={togglePerUnit} className="h-4 w-4 accent-sky-700" data-testid="barcode-per-unit-toggle" />
              One label per unit <span className="text-slate-400 text-xs">(1 of N)</span>
            </label>
            {perUnit && (
              <div className="flex items-center gap-1.5 h-9 px-3 rounded-md border border-slate-300 bg-white text-sm text-slate-700" title="Reprint only some pieces, e.g. 4 to 10 of 10" data-testid="barcode-piece-range">
                <span className="text-slate-500 text-xs">Pieces</span>
                <Input type="number" min="1" value={pieceFrom} placeholder="from" onChange={(e) => setPieceFrom(e.target.value)} onBlur={() => applyRange(pieceFrom, pieceTo)} onKeyDown={(e) => e.key === "Enter" && applyRange(pieceFrom, pieceTo)} className="h-7 w-16 text-xs text-center border-slate-200 px-1" data-testid="barcode-piece-from" />
                <span className="text-slate-400">–</span>
                <Input type="number" min="1" value={pieceTo} placeholder="to" onChange={(e) => setPieceTo(e.target.value)} onBlur={() => applyRange(pieceFrom, pieceTo)} onKeyDown={(e) => e.key === "Enter" && applyRange(pieceFrom, pieceTo)} className="h-7 w-16 text-xs text-center border-slate-200 px-1" data-testid="barcode-piece-to" />
                {(pieceFrom || pieceTo) && <button type="button" onClick={() => { setPieceFrom(""); setPieceTo(""); applyRange("", ""); }} className="text-xs text-sky-700 font-medium hover:underline ml-1" data-testid="barcode-piece-clear">all</button>}
              </div>
            )}
            {mode === "po" && gate?.labels?.length > 0 && (
              <div className="flex gap-2 ml-auto">
                <Button onClick={printLabels} data-testid="barcode-print-btn" className="bg-emerald-700 hover:bg-emerald-800 text-white"><Printer className="h-4 w-4 mr-1.5" />Print Labels</Button>
                <Button onClick={downloadZpl} disabled={zplBusy} variant="outline" data-testid="barcode-zpl-btn" className="border-slate-300 text-slate-700"><FileDown className="h-4 w-4 mr-1.5" />{zplBusy ? "…" : "Download ZPL"}</Button>
              </div>
            )}
          </div>

          {mode === "po" && (
            <>
              <GateInPanel perUnit={perUnit} pieceRange={pieceRange} onLabels={setGate} loading={loading} setLoading={setLoading} />
              {gate?.labels?.length > 0 && (
                <>
                  <div className="no-print rounded-lg border border-slate-200 bg-white px-4 py-3 shadow-sm flex flex-wrap gap-x-6 gap-y-1 text-sm" data-testid="gatein-labels-header">
                    <span className="text-slate-500">Gate In · PO: <b className="text-slate-800 font-mono">{gate.po}</b></span>
                    {gate.reprint && <span className="rounded-full bg-slate-900 text-white text-[10px] font-bold px-2 py-0.5" data-testid="gatein-reprint-badge">REPRINT</span>}
                    <span className="text-slate-500">Date: <b className="text-slate-800">{gate.gate_in_date}</b></span>
                    <span className="text-slate-500">Lines: <b className="text-slate-800">{gate.lines.length}</b></span>
                    <span className="text-slate-500">Labels: <b className="text-slate-800" data-testid="gatein-label-count">{labels.length}</b>{perUnit && <span className="text-slate-400 text-xs"> (per unit)</span>}</span>
                    <span className="text-slate-500">Copies / label: <b className="text-slate-800">{copies}</b></span>
                    <span className="text-slate-400">= {printItems.length} print(s)</span>
                  </div>
                  {splitPanel}
                  <div id="barcode-print-area">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="gatein-label-grid">
                      {printItems.map((it, i) => (
                        <LabelCard key={`g-${it.item_no}-${it.piece || 0}-${it._copy}-${i}`} it={it} doc="" year="" />
                      ))}
                    </div>
                  </div>
                </>
              )}
            </>
          )}

          {mode === "matdoc" && (<>
          <div className="no-print flex flex-wrap items-end gap-3 rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
            <div>
              <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Material Document No. (GRN / Production)</label>
              <Input value={docNo} onChange={(e) => setDocNo(e.target.value)} onKeyDown={(e) => e.key === "Enter" && fetchDoc()}
                placeholder="e.g. 5000012345 (GRN) or 4900074854 (Issue)" className="h-9 w-80 border-slate-300 text-sm font-mono" data-testid="barcode-doc-input" />
            </div>
            <div>
              <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Year (optional)</label>
              <Input value={year} onChange={(e) => setYear(e.target.value)} onKeyDown={(e) => e.key === "Enter" && fetchDoc()}
                placeholder="e.g. 2026" className="h-9 w-28 border-slate-300 text-sm font-mono" data-testid="barcode-year-input" />
            </div>
            <Button onClick={() => fetchDoc()} disabled={loading} data-testid="barcode-fetch-btn" className="bg-sky-700 hover:bg-sky-800 text-white">
              <Search className="h-4 w-4 mr-1.5" />{loading ? "Fetching…" : "Fetch from SAP"}
            </Button>
            <label className="flex items-center gap-2 h-9 px-3 rounded-md border border-slate-300 bg-slate-50 text-sm text-slate-700 cursor-pointer select-none" title="Only receipt lines (101/105/501…) — skips component issues such as 261 on production documents">
              <input type="checkbox" checked={inwardOnly} onChange={toggleInwardOnly} className="h-4 w-4 accent-sky-700" data-testid="barcode-inward-only-toggle" />
              Inward lines only
            </label>
            {items.length > 0 && (
              <div className="flex gap-2 ml-auto">
                <Button onClick={printLabels} data-testid="barcode-print-btn" className="bg-emerald-700 hover:bg-emerald-800 text-white">
                  <Printer className="h-4 w-4 mr-1.5" />Print Labels
                </Button>
                <Button onClick={downloadZpl} disabled={zplBusy} variant="outline" data-testid="barcode-zpl-btn" className="border-slate-300 text-slate-700">
                  <FileDown className="h-4 w-4 mr-1.5" />{zplBusy ? "…" : "Download ZPL"}
                </Button>
              </div>
            )}
          </div>

          {result?.source === "demo" && (
            <div className="no-print rounded-lg border border-amber-200 bg-amber-50 p-3 flex items-start gap-2" data-testid="barcode-demo-note">
              <Info className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
              <p className="text-xs text-amber-800">No live SAP connection configured — showing <b>demo</b> items so you can preview label layout &amp; printing. Connect SAP in Settings to pull real Material Documents.</p>
            </div>
          )}

          {result && items.length > 0 && labels.length > 0 && (
            <>
              <div className="no-print rounded-lg border border-slate-200 bg-white px-4 py-3 shadow-sm flex flex-wrap gap-x-6 gap-y-1 text-sm" data-testid="barcode-doc-header">
                <span className="text-slate-500">Material Document: <b className="text-slate-800 font-mono">{result.doc}/{result.header?.year || year}</b></span>
                {result.header?.posting_date && <span className="text-slate-500">Posting Date: <b className="text-slate-800">{result.header.posting_date}</b></span>}
                <span className="text-slate-500">Items: <b className="text-slate-800">{items.length}</b>{inwardOnly && result?.non_inward_lines > 0 && <span className="text-slate-400 text-xs"> ({result.non_inward_lines} issue line{result.non_inward_lines === 1 ? "" : "s"} skipped)</span>}</span>
                <span className="text-slate-500">Labels: <b className="text-slate-800" data-testid="barcode-label-count">{labels.length}</b>{perUnit && <span className="text-slate-400 text-xs"> (per unit)</span>}</span>
                <span className="text-slate-500">Copies / label: <b className="text-slate-800">{copies}</b></span>
                <span className="text-slate-400">= {printItems.length} print(s)</span>
              </div>
              {wbsHint && (
                <div className="no-print rounded-lg border border-amber-200 bg-amber-50 p-3 flex items-start gap-2" data-testid="barcode-wbs-hint">
                  <Info className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                  <p className="text-xs text-amber-800">{wbsHint} Until then, descriptions fall back to the synced WBS/project master where available.</p>
                </div>
              )}
              {orderHint && (
                <div className="no-print rounded-lg border border-amber-200 bg-amber-50 p-3 flex items-start gap-2" data-testid="barcode-order-hint">
                  <Info className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                  <p className="text-xs text-amber-800">{orderHint}</p>
                </div>
              )}

              {splitPanel}
              <div id="barcode-print-area">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="barcode-label-grid">
                  {printItems.map((it, i) => (
                    <LabelCard key={`${it.item_no}-${it.piece || 0}-${it._copy}-${i}`} it={it} doc={result.doc} year={result.header?.year || year} />
                  ))}
                </div>
              </div>
            </>
          )}

          {result && items.length === 0 && (
            <div className="no-print rounded-lg border border-slate-200 bg-white p-8 text-center text-sm text-slate-400" data-testid="barcode-empty">
              No items found for this Material Document. Check the number (and year) and try again.
            </div>
          )}
          {result && items.length > 0 && labels.length === 0 && (
            <div className="no-print rounded-lg border border-amber-200 bg-amber-50 p-6 text-center text-sm text-amber-800" data-testid="barcode-no-inward">
              This document has no inward (receipt) lines — untick “Inward lines only” to label its issue lines anyway.
            </div>
          )}
          </>)}
        </>
      ) : tab === "register" ? (
        <div className="no-print"><GateInRegister onOverdue={setOverdueGateIns} onReprint={reprintGateIn} onOpenDoc={openDocFromRegister} /></div>
      ) : tab === "scan" ? (
        <div className="no-print"><ScanLookup /></div>
      ) : (
        <Card className="border-slate-200 shadow-sm max-w-2xl no-print" data-testid="barcode-config-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg font-semibold text-slate-800 flex items-center gap-2"><Settings2 className="h-5 w-5 text-sky-700" />Barcode Printer Configuration</CardTitle>
            <p className="text-xs text-slate-400 mt-0.5">These settings drive the ZPL label size, print darkness and number of copies. Printer name / IP is saved for your reference and used when printing directly on your on-prem deployment.</p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              {[
                ["label_width_mm", "Label Width (mm)", "number"],
                ["label_height_mm", "Label Height (mm)", "number"],
                ["dpi", "Printer DPI (203 / 300 / 600)", "number"],
                ["darkness", "Darkness (0–30)", "number"],
                ["copies", "Copies per Item", "number"],
                ["printer_name", "Printer Name", "text"],
                ["printer_ip", "Printer IP Address", "text"],
              ].map(([key, label, type]) => (
                <div key={key}>
                  <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">{label}</label>
                  <Input type={type} value={cfg[key] ?? ""} disabled={!admin}
                    onChange={(e) => setCfg((c) => ({ ...c, [key]: e.target.value }))}
                    className="h-9 border-slate-300 text-sm" data-testid={`barcode-cfg-${key}`} />
                </div>
              ))}
            </div>
            {admin ? (
              <Button onClick={saveCfg} disabled={savingCfg} data-testid="barcode-cfg-save" className="bg-sky-700 hover:bg-sky-800 text-white">
                <Save className="h-4 w-4 mr-1.5" />{savingCfg ? "Saving…" : "Save Configuration"}
              </Button>
            ) : (
              <p className="text-xs text-slate-400">Only admins can change the printer configuration.</p>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
